import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Brain, Loader2, Sparkles, ArrowRight, ChevronRight, Lock, Zap, FolderOpen, Send, MessageSquare, Globe, ExternalLink, Search } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AIAnalystPage = () => {
  const { user, hasActivePlan } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState('describe'); // describe | clarifying | researching | generating | done
  const [description, setDescription] = useState('');
  const [projectName, setProjectName] = useState('');
  const [selectedFolder, setSelectedFolder] = useState('__default__');
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generatedProjectId, setGeneratedProjectId] = useState(null);
  const [explanation, setExplanation] = useState(null);
  const [sources, setSources] = useState([]);
  const [usage, setUsage] = useState(null);
  // Clarification flow
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [sessionId] = useState(() => `gen-${Date.now()}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (hasActivePlan()) {
      fetchUsage();
      fetchFolders();
    }
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [questions, step]);

  const fetchUsage = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(`${API}/api/ai-analyst/usage`, { headers: { Authorization: `Bearer ${token}` } });
      setUsage(res.data);
    } catch (e) { /* ignore */ }
  };

  const fetchFolders = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(`${API}/api/folders`, { headers: { Authorization: `Bearer ${token}` } });
      setFolders(res.data || []);
    } catch (e) { /* ignore */ }
  };

  const handleAnalyze = async () => {
    if (!description.trim() || description.trim().length < 20) {
      toast.error('Describe tu negocio con mas detalle (minimo 20 caracteres)');
      return;
    }
    setLoading(true);
    setStep('clarifying');
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.post(`${API}/api/ai-analyst/clarify`, {
        description: description.trim(),
        session_id: sessionId,
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });

      const q = res.data.questions || [];
      if (q.length === 0) {
        // No questions needed, generate directly
        handleGenerate([]);
        return;
      }
      setQuestions(q);
      setAnswers({});
    } catch (err) {
      toast.error('Error al analizar. Intenta de nuevo.');
      setStep('describe');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (clarificationAnswers) => {
    setLoading(true);
    setStep('researching');
    try {
      const token = localStorage.getItem('auth_token');
      const body = {
        description: description.trim(),
        session_id: sessionId,
      };
      if (clarificationAnswers && clarificationAnswers.length > 0) {
        body.clarification_answers = clarificationAnswers;
      }
      if (projectName.trim()) body.project_name = projectName.trim();
      if (selectedFolder === '__default__') {
        body.create_default_folder = true;
      } else if (selectedFolder !== '__none__') {
        body.folder_id = selectedFolder;
      }

      // Show "researching" for a bit, then switch to "generating"
      const researchTimer = setTimeout(() => setStep('generating'), 15000);

      const res = await axios.post(`${API}/api/ai-analyst/generate`, body, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 180000
      });
      clearTimeout(researchTimer);
      setGeneratedProjectId(res.data.project_id);

      // Fetch explanation
      try {
        const expRes = await axios.get(`${API}/api/projects/${res.data.project_id}/ai-explanation`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setExplanation(expRes.data.explanation);
      } catch (e) { /* ignore */ }

      // Fetch sources
      try {
        const srcRes = await axios.get(`${API}/api/projects/${res.data.project_id}/research-sources`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSources(srcRes.data.sources || []);
      } catch (e) { /* ignore */ }

      setStep('done');
      toast.success(res.data.message);
      fetchUsage();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error al generar el proyecto';
      toast.error(detail);
      setStep('describe');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAnswers = () => {
    const qa = questions.map((q, i) => ({
      question: q,
      answer: answers[i] || ''
    })).filter(qa => qa.answer.trim());
    handleGenerate(qa);
  };

  const allAnswered = questions.length > 0 && questions.every((_, i) => answers[i]?.trim());

  const examples = [
    'Quiero poner una cafeteria de especialidad en Monterrey, Mexico. Inversion de $800,000 MXN. Venta estimada de 80 cafes al dia a $65 pesos y 30 postres a $85. Renta $25,000 mensuales, 3 empleados.',
    'Negocio de lavanderia autoservicio en Guadalajara. 10 lavadoras y 10 secadoras industriales. Cobro $80 por lavado y $60 por secado. 40 servicios diarios. Local en renta $18,000/mes.',
    'Taller mecanico automotriz en CDMX. Inversion en equipo $500,000. Servicios: cambio de aceite $350, frenos $1,200, afinacion $2,500. 8 servicios diarios. 4 mecanicos.',
  ];

  if (!user || !hasActivePlan()) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-2xl bg-blue-500/10 flex items-center justify-center mx-auto mb-5">
            <Lock className="w-8 h-8 text-blue-400" />
          </div>
          <h1 className="text-2xl font-bold mb-3">Analista IA</h1>
          <p className="text-muted-foreground mb-6">Esta funcion esta disponible para usuarios con plan activo. Describe tu negocio y la IA genera todo el modelo financiero.</p>
          <Button onClick={() => navigate('/pricing')} className="bg-blue-600 hover:bg-blue-700" data-testid="ai-go-pricing">
            Ver planes <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold" data-testid="ai-analyst-title">Analista IA</h1>
              <p className="text-sm text-muted-foreground">Tu analista financiero personal</p>
            </div>
          </div>
          {usage && (
            <div className="text-right text-xs text-muted-foreground" data-testid="ai-usage-info">
              <div>Generaciones: {usage.generation.used}/{usage.generation.limit}{usage.bonus?.generations > 0 && <span className="text-amber-400 ml-1">(+{usage.bonus.generations} extra)</span>}</div>
              <div>Modificaciones: {usage.modification.used}/{usage.modification.limit}{usage.bonus?.modifications > 0 && <span className="text-amber-400 ml-1">(+{usage.bonus.modifications} extra)</span>}</div>
              {(usage.generation.used >= usage.generation.limit - 2 || usage.modification.used >= usage.modification.limit - 2) && (
                <button onClick={() => navigate('/pricing')} className="text-amber-400 hover:text-amber-300 underline mt-1 transition-colors">Comprar Pack Extra</button>
              )}
            </div>
          )}
        </div>

        {step === 'describe' && (
          <div className="space-y-5 animate-fade-in">
            {/* Name and Folder */}
            <Card className="p-5 border-border/40">
              <h2 className="font-semibold mb-3 text-sm text-muted-foreground uppercase tracking-wider">Configuracion del proyecto</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Nombre del proyecto</label>
                  <input
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    placeholder="Ej: Cafeteria Monterrey (opcional)"
                    className="w-full bg-background border border-border/60 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 placeholder:text-muted-foreground/50"
                    data-testid="ai-project-name"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Si lo dejas vacio, la IA le pondra nombre</p>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Carpeta</label>
                  <Select value={selectedFolder} onValueChange={setSelectedFolder}>
                    <SelectTrigger className="w-full" data-testid="ai-folder-select">
                      <SelectValue placeholder="Selecciona carpeta" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__default__">
                        <span className="flex items-center gap-2"><FolderOpen className="w-3.5 h-3.5 text-purple-400" /> Proyectos IA (automatica)</span>
                      </SelectItem>
                      {folders.map(f => (
                        <SelectItem key={f.id} value={f.id}>
                          <span className="flex items-center gap-2"><FolderOpen className="w-3.5 h-3.5" /> {f.name}</span>
                        </SelectItem>
                      ))}
                      <SelectItem value="__none__">Sin carpeta</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">Puedes moverlo despues</p>
                </div>
              </div>
            </Card>

            {/* Description */}
            <Card className="p-5 border-border/40">
              <h2 className="font-semibold mb-2 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-blue-400" />
                Describe tu negocio
              </h2>
              <p className="text-sm text-muted-foreground mb-4">
                Incluye tipo de negocio, ubicacion, inversion estimada, productos/servicios, precios y gastos que conozcas. Mientras mas detalle, mejor sera el modelo.
              </p>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Ej: Quiero poner una cafeteria en Monterrey, inversion de $800,000..."
                className="w-full h-36 bg-background border border-border/60 rounded-xl p-4 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/40 placeholder:text-muted-foreground/50"
                data-testid="ai-description-input"
              />
              <div className="flex items-center justify-between mt-4">
                <span className="text-xs text-muted-foreground">{description.length} caracteres</span>
                <Button
                  onClick={handleAnalyze}
                  disabled={loading || description.trim().length < 20}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 gap-2"
                  data-testid="ai-analyze-btn"
                >
                  <Brain className="w-4 h-4" /> Analizar con IA
                </Button>
              </div>
            </Card>

            {/* Examples */}
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">Ejemplos para inspirarte:</h3>
              <div className="space-y-2">
                {examples.map((ex, i) => (
                  <button
                    key={i}
                    onClick={() => setDescription(ex)}
                    className="w-full text-left p-3 rounded-lg border border-border/30 hover:border-purple-500/30 hover:bg-purple-500/5 transition-all text-sm text-muted-foreground group"
                    data-testid={`ai-example-${i}`}
                  >
                    <ChevronRight className="w-3 h-3 inline mr-1 text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {ex.substring(0, 120)}...
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 'clarifying' && (
          <div className="space-y-5 animate-fade-in" ref={scrollRef}>
            {/* User's description shown as context */}
            <Card className="p-4 border-border/40 bg-blue-500/5 border-blue-500/20">
              <p className="text-xs text-blue-400 font-medium mb-1">Tu descripcion:</p>
              <p className="text-sm text-muted-foreground">{description}</p>
            </Card>

            {loading && !questions.length ? (
              <div className="flex items-center gap-3 py-8 justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-purple-400" />
                <span className="text-sm text-muted-foreground">La IA esta analizando tu descripcion...</span>
              </div>
            ) : (
              <>
                {/* AI Questions */}
                <Card className="p-5 border-border/40">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                      <MessageSquare className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">Antes de generar, necesito aclarar unas cosas:</p>
                      <p className="text-[11px] text-muted-foreground">Responde para que el modelo sea lo mas preciso posible</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {questions.map((q, i) => (
                      <div key={i} className="space-y-2">
                        <label className="text-sm font-medium flex items-center gap-2">
                          <span className="w-5 h-5 rounded-full bg-purple-500/20 text-purple-400 text-xs flex items-center justify-center font-bold">{i + 1}</span>
                          {q}
                        </label>
                        <input
                          value={answers[i] || ''}
                          onChange={(e) => setAnswers(prev => ({ ...prev, [i]: e.target.value }))}
                          placeholder="Tu respuesta..."
                          className="w-full bg-background border border-border/50 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/40 placeholder:text-muted-foreground/50"
                          data-testid={`ai-answer-${i}`}
                          autoFocus={i === 0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && allAnswered) handleSubmitAnswers();
                          }}
                        />
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center justify-between mt-5 pt-4 border-t border-border/20">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setStep('describe'); setQuestions([]); setAnswers({}); }}
                      data-testid="ai-back-btn"
                    >
                      Volver a editar
                    </Button>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleGenerate([])}
                        data-testid="ai-skip-btn"
                      >
                        Omitir y generar
                      </Button>
                      <Button
                        onClick={handleSubmitAnswers}
                        disabled={!allAnswered}
                        className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 gap-2"
                        data-testid="ai-generate-with-answers-btn"
                      >
                        <Zap className="w-4 h-4" /> Generar modelo
                      </Button>
                    </div>
                  </div>
                </Card>
              </>
            )}
          </div>
        )}

        {(step === 'researching' || step === 'generating') && (
          <div className="flex flex-col items-center justify-center py-20 animate-fade-in">
            <div className={`w-20 h-20 rounded-2xl flex items-center justify-center mb-6 animate-pulse ${
              step === 'researching' ? 'bg-gradient-to-br from-emerald-500 to-teal-600' : 'bg-gradient-to-br from-blue-500 to-purple-600'
            }`}>
              {step === 'researching' ? <Globe className="w-10 h-10 text-white" /> : <Brain className="w-10 h-10 text-white" />}
            </div>
            <h2 className="text-xl font-bold mb-2">
              {step === 'researching' ? 'Investigando datos de mercado...' : 'Generando tu modelo financiero...'}
            </h2>
            <p className="text-muted-foreground text-center max-w-md mb-6">
              {step === 'researching'
                ? 'La IA esta buscando precios reales, rentas, salarios y costos en la web para tu region. Esto puede tomar 1-2 minutos, pero la espera vale la pena.'
                : 'Construyendo el modelo financiero con datos reales de mercado. Ya casi esta listo.'}
            </p>

            {/* Progress steps */}
            <div className="flex flex-col gap-3 w-full max-w-sm">
              <div className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border ${step === 'researching' ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-emerald-500/20 bg-emerald-500/5 opacity-60'}`}>
                {step === 'researching' ? <Search className="w-4 h-4 text-emerald-400 animate-pulse" /> : <Sparkles className="w-4 h-4 text-emerald-400" />}
                <span className="text-sm text-emerald-300">Investigando precios y costos reales</span>
              </div>
              <div className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border ${step === 'generating' ? 'border-blue-500/30 bg-blue-500/10' : 'border-border/20 opacity-40'}`}>
                {step === 'generating' ? <Loader2 className="w-4 h-4 text-blue-400 animate-spin" /> : <Brain className="w-4 h-4 text-muted-foreground" />}
                <span className={`text-sm ${step === 'generating' ? 'text-blue-300' : 'text-muted-foreground'}`}>Generando modelo financiero</span>
              </div>
            </div>
          </div>
        )}

        {step === 'done' && generatedProjectId && (
          <div className="space-y-6 animate-fade-in">
            <div className="flex flex-col items-center text-center py-8">
              <div className="w-16 h-16 rounded-2xl bg-emerald-500/20 flex items-center justify-center mb-5">
                <Sparkles className="w-8 h-8 text-emerald-400" />
              </div>
              <h2 className="text-xl font-bold mb-2">Modelo financiero generado</h2>
              <p className="text-muted-foreground max-w-md mb-6">
                Tu proyecto ha sido creado con todas las inversiones, productos, costos y gastos.
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={() => navigate(`/app/project/${generatedProjectId}/dashboard`)}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 gap-2"
                  data-testid="ai-go-dashboard-btn"
                >
                  Ver Dashboard <ArrowRight className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setStep('describe'); setDescription(''); setProjectName(''); setGeneratedProjectId(null); setExplanation(null); setQuestions([]); setAnswers({}); }}
                  data-testid="ai-new-project-btn"
                >
                  Crear otro proyecto
                </Button>
              </div>
            </div>

            {/* AI Explanation */}
            {explanation && (
              <Card className="p-6 border-purple-500/20 bg-purple-500/5">
                <div className="flex items-center gap-2 mb-4">
                  <Brain className="w-5 h-5 text-purple-400" />
                  <h3 className="font-semibold text-purple-300">Analisis del Analista IA</h3>
                </div>
                <div className="prose prose-sm prose-invert max-w-none text-gray-300 [&>h2]:text-purple-300 [&>h2]:text-base [&>h2]:mt-4 [&>h2]:mb-2 [&>strong]:text-white [&>ul]:ml-4 [&>ol]:ml-4 [&>p]:mb-2">
                  <ReactMarkdown>{explanation}</ReactMarkdown>
                </div>
              </Card>
            )}

            {/* Research Sources */}
            {sources.length > 0 && (
              <Card className="p-6 border-emerald-500/20 bg-emerald-500/5" data-testid="research-sources">
                <div className="flex items-center gap-2 mb-4">
                  <Globe className="w-5 h-5 text-emerald-400" />
                  <h3 className="font-semibold text-emerald-300">Fuentes de investigacion</h3>
                  <span className="text-xs text-muted-foreground ml-auto">{sources.length} fuentes consultadas</span>
                </div>
                <p className="text-xs text-muted-foreground mb-3">Estas son las fuentes web que la IA consulto para obtener datos reales de mercado:</p>
                <div className="space-y-2">
                  {sources.map((s, i) => (
                    <a
                      key={i}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-2 p-2 rounded-lg hover:bg-emerald-500/10 transition-colors group"
                    >
                      <ExternalLink className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0 opacity-50 group-hover:opacity-100" />
                      <div className="min-w-0">
                        <p className="text-sm text-gray-300 group-hover:text-emerald-300 line-clamp-1">{s.title}</p>
                        <p className="text-[11px] text-muted-foreground/60 truncate">{s.url}</p>
                      </div>
                    </a>
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AIAnalystPage;
